// Shared injection keys used for dependency injection between parent/child components or pages
// See: https://vuejs.org/api/composition-api-dependency-injection.html
// and: https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject

import type { ShallowRef } from "vue";

export interface SearchTabsContext {
    tab: Readonly<Ref<string | number | undefined>>;
    setTab: (value: string | number) => void;
}

export const SearchInjectKey: InjectionKey<SearchTabsContext> = Symbol("SearchTabs");

export interface SearchIds {
    distributorIds: ShallowRef<Set<number>>;
    manufacturerIds: ShallowRef<Set<number>>;
    partKeys: ShallowRef<Set<string>>;
    sortOrders: ShallowRef<Set<string>>;
    clickHash: Ref<string>;
}

export const SearchIdsInjectKey: InjectionKey<SearchIds> = Symbol("SearchIds");

export interface CategoryContext {
    category: Ref<Category | null>;
    filterOptions: Ref<FilterOption[] | null>;
    filterValues: Ref<FilterValues>;
    manufacturers: Ref<Manufacturer[] | null>;
}

export const CategoryInjectKey: InjectionKey<CategoryContext> = Symbol("Category");

export function injectCategory() {
    return inject(CategoryInjectKey, {
        category: ref(null),
        filterOptions: ref(null),
        filterValues: ref({ inStock: false, manufacturers: [], specs: [] }),
        manufacturers: ref([]),
    });
}

export interface BOMContext {
    bomSearch: Ref<BOMSearch | null>;
    isReadOnly: ComputedRef<boolean>;
}

export const BOMInjectKey: InjectionKey<BOMContext> = Symbol("BOM");

export function injectBOM() {
    return inject(BOMInjectKey, {
        bomSearch: ref(null),
        isReadOnly: computed(() => false),
    });
}

export type BomTableFilters = {
    PartNumber?: string;
    ManufacturerName?: string;
    HideOutOfStock: boolean;
};

export type BomTableDragState = {
    startIndex: number;
    targetIndex: number;
    isActive: boolean;
};

export interface BOMTableContext {
    columnState: BomTableDragState;
    rowState: BomTableDragState;
    filters: BomTableFilters;
    showPriceBreaks: Ref<boolean>;
    showReferenceNumbers: Ref<boolean>;
    page: Ref<number>;
    pageSize: Ref<number>;
}

export const BOMTableInjectKey: InjectionKey<BOMTableContext> = Symbol("BOMTable");

export function injectBOMTable() {
    return inject(BOMTableInjectKey, {
        columnState: reactive<BomTableDragState>({ startIndex: -1, targetIndex: -1, isActive: false }),
        rowState: reactive<BomTableDragState>({ startIndex: -1, targetIndex: -1, isActive: false }),
        filters: reactive<BomTableFilters>({ HideOutOfStock: false }),
        showPriceBreaks: ref(false),
        showReferenceNumbers: ref(false),
        page: ref(0),
        pageSize: ref(0),
    });
}
